import {
  getCommunity,
  deleteCommunity,
  updateCommunity,
  addCommunity,
} from '@/adapters/master';
import { IDataModal } from '@/interfaces/data-modal';
import { ICommunity, ICommunityRes } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Flex,
  Form,
  Modal,
  notification,
  Radio,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import { useEffect, useMemo, useState, useCallback, Suspense } from 'react';
import { uniqueId } from 'lodash';

const _modal: IDataModal<ICommunity> = {
  open: false,
};

const CommunityPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<ICommunity[]>([]);
  const [action, setAction] = useState<'create' | 'update' | 'match'>('create');
  const [selected, setSelected] = useState<ICommunity[]>([]);
  const [modal, setModal] = useState<IDataModal<ICommunity>>({ ..._modal });
  const [form] = Form.useForm();
  const [tab, setTab] = useState<'create' | 'update'>('create');

  // const { data, refetch } = useQuery<IBrandRes, Error>({
  //   queryKey: ['search-brand-list'],
  //   queryFn: searchBrands,
  //   retry: 0,
  // });
  const { data, refetch } = useQuery<ICommunityRes, Error>({
    queryKey: ['get-community'],
    queryFn: getCommunity,
    retry: 0,
  });

  const actionCommunityMutation = useMutation({
    mutationFn: async (data: Partial<ICommunity>) =>
      tab === 'create' ? await addCommunity(data) : await updateCommunity(data),
    mutationKey: tab === 'create' ? ['add-Community'] : ['update-Community'],
    onSuccess: data => {
      if (data.status === 'fulfilled' && tab === 'create') {
        notification.success({ message: 'Sync Community สำเร็จ' });
        refetch();
      } else if (data?.data[0].status === 'fulfilled' && tab === 'update') {
        notification.success({ message: 'Update Community สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: data.reason?.errors?.errorMessage });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleSyncSelectedCommunity = async (selectedRows: any) => {
    await actionCommunityMutation.mutateAsync(selectedRows);
  };

  const actionDeleteCommunity = useMutation({
    mutationFn: async (data: Partial<ICommunity>) =>
      await deleteCommunity(data),
    mutationKey: ['delete-Community'],
    onSuccess: data => {
      if (data.status === 'OK' && tab === 'update') {
        notification.success({ message: 'Delete Community สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: 'Delete Community ไม่สำเร็จ' });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  // const handleSyncTOERP = async () => {
  //   await actionTOERP.mutateAsync();
  // };

  const handleDeleteCommunity = async (selectedRows: any) => {
    await actionDeleteCommunity.mutateAsync(selectedRows);
  };

  const handleUpdateCommunityCode = useCallback((item: ICommunity) => {
    setModal({
      open: true,
      item: item,
    });
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          title: 'ลำดับ',
          dataIndex: '',
          width: 40,
          align: 'center' as const,
          className: 'text-bold',
          render: (_: any, __: ICommunity, i: number) => (
            <div className="font-weight-bold">{i + 1}</div>
          ),
        },
        {
          title: 'รหัสวิสาหกิจชุมชน',
          dataIndex: 'SI_SITE',
          defaultSortOrder: 'ascend',
          sorter: (a, b) =>
            Number(a.SI_SITE?.replace(/\D/g, '').replace('-', '')) -
            Number(b.SI_SITE?.replace(/\D/g, '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.SI_SITE)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.SI_SITE?.startsWith(value as string);
          },
        },
        {
          title: 'ชื่อวิสาหกิจชุมชน',
          dataIndex: 'SI_DESC',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.SI_DESC)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.SI_DESC?.startsWith(value as string);
          },
        },
        {
          title: 'จังหวัด',
          dataIndex: 'SI_CITY_CODE',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.SI_CITY_CODE)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.SI_CITY_CODE?.startsWith(value as string);
          },
        },
        {
          title: 'จำนวนสมาชิก',
          dataIndex: 'SI_MEMBER_QTY',
        },
      ] as TableColumnsType<ICommunity>,
    [tab, selected, handleUpdateCommunityCode],
  );

  const insert = data?.insert;
  const update = data?.update;
  const match = data?.match;
  useEffect(() => {
    const listMap = { create: insert, update, match };
    setSelected(listMap[tab]?.map(c => ({ ...c, _key: uniqueId('b-') })) || []);
    setSelectedRowKeys([]);
  }, [tab, insert, update, match]);

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: ICommunity[]) => {
      setSelectedRowKeys(selectedRows.map(row => row._key));
      setSelectedRows(selectedRows);
    },
    selectedRowKeys,
    getCheckboxProps: (record: ICommunity) => ({
      disabled: action === 'match',
    }),
  };

  const [search, SetSearch] = useState('');
  const [searchText1, setSearchText1] = useState('');
  const [searchText2, setSearchText2] = useState('');

  const filteredData = useMemo(() => {
    return selected.filter(item => {
      const itemCode = (item.SI_SITE || '').toLowerCase();
      const searchValue = search.trim().toLowerCase();
      return !searchValue || itemCode.includes(searchValue);
    });
  }, [selected, action, search, searchText1, searchText2]);

  // const onFinish: FormProps<IBrand>['onFinish'] = async values => {
  //   await matchBrandApi.mutateAsync({
  //     ...values,
  //     BRAND_SING_ID: modal.item?.BRAND_SING_ID,
  //   });
  // };

  return (
    <div>
      <Flex justify="space-between" gap={2} className="mb-3">
        <Flex gap={0}>
          <Radio.Group value={tab} onChange={e => setTab(e.target.value)}>
            <Radio.Button value="create">เพิ่ม</Radio.Button>
            <Radio.Button value="update">แก้ไข/ปรับปรุง</Radio.Button>
          </Radio.Group>
        </Flex>
        <Flex gap={12}>
          {tab === 'update' ? (
            <button
              type="button"
              className="btn btn-info"
              style={{ minWidth: 100 }}
              onClick={() => handleDeleteCommunity(selectedRows)}
            >
              {actionDeleteCommunity.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'ลบข้อมูล Community'
              )}
            </button>
          ) : (
            <></>
          )}

          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 100 }}
            disabled={!selectedRowKeys.length}
            onClick={() => handleSyncSelectedCommunity(selectedRows)}
          >
            {actionCommunityMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : tab === 'create' ? (
              'ส่งข้อมูลไป OTOD >'
            ) : (
              'แก้ไขข้อมูลที่ OTOD >'
            )}
          </button>
        </Flex>
      </Flex>
      <Table<ICommunity>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={columns}
        dataSource={filteredData}
        size="small"
        bordered
        rowKey={r => r._key}
        pagination={false}
        locale={{ emptyText: 'ไม่พบรายการ' }}
      />
      <Suspense>
        <Modal
          open={modal.open}
          width={350}
          onCancel={() => setModal({ ..._modal })}
          // loading={matchBrandApi.isPending}
          // onOk={() => {
          //   form
          //     .validateFields()
          //     .then(values => {
          //       onFinish(values);
          //     })
          // .catch(() => {
          //       // validation failed
          //     });
          // }}
        >
          <Form
            form={form}
            name="land"
            layout="vertical"
            // onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item<ICommunity>
              label="SI_SITE"
              name="SI_SITE"
              rules={[{ required: true }]}
            >
              <Select
                style={{ minWidth: 150 }}
                size="large"
                showSearch
                allowClear
                placeholder="ค้นหา"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[...(insert || [])].sort().map(item => ({
                  label: `${item.SI_SITE} - ${item.SI_SITE}`,
                  value: item.SI_SITE,
                }))}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Suspense>
    </div>
  );
};

export default CommunityPage;
